import {
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_TITLE,
  DEFAULT_OG_IMAGE_PATH,
  THEME_COLOR,
  buildPageTitle,
  buildChannelDescription,
  buildChannelKeywords,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  buildVideoObjectJsonLd,
  getSiteBaseUrl,
  resolveAbsoluteAssetUrl,
  resolveAbsoluteSiteUrl,
  resolveChannelPosterUrl,
  resolveChannelOgImageDimensions
} from './seoConfig.js';

function ensureMetaTag(name) {
  try {
    let element = document.querySelector(`meta[name="${CSS.escape(name)}"]`);
    if (element) return element;
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
    return element;
  } catch {
    return null;
  }
}

function ensureMetaProperty(property) {
  try {
    let element = document.querySelector(`meta[property="${CSS.escape(property)}"]`);
    if (element) return element;
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
    return element;
  } catch {
    return null;
  }
}

function ensureLinkRel(rel) {
  try {
    let element = document.querySelector(`link[rel="${CSS.escape(rel)}"]`);
    if (element) return element;
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
    return element;
  } catch {
    return null;
  }
}

function ensureJsonLdScript(id) {
  try {
    let element = document.getElementById(id);
    if (element) return element;
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
    return element;
  } catch {
    return null;
  }
}

function setMetaContent(element, content) {
  if (element && content !== undefined && content !== null) {
    element.setAttribute('content', String(content));
  }
}

function removeJsonLdScript(id) {
  try {
    const element = document.getElementById(id);
    if (element) element.remove();
  } catch {
    // ignore
  }
}

export function updateDocumentSeo({
  channel,
  path = '/',
  title,
  description,
  imagePath = DEFAULT_OG_IMAGE_PATH,
  imageAlt
} = {}) {
  try {
    const pageTitle = title || buildPageTitle(channel?.title || channel?.slug);
    const pageDescription = description || buildChannelDescription(channel);
    const canonicalUrl = resolveAbsoluteSiteUrl(path);
    const posterUrl = channel ? resolveChannelPosterUrl(channel) : '';
    const imageUrl = posterUrl || resolveAbsoluteAssetUrl(imagePath);
    const resolvedImageAlt =
      imageAlt || (channel ? (channel.title || channel.slug || SITE_NAME) : `${SITE_NAME} logo`);
    const { width: imageWidth, height: imageHeight } = channel
      ? resolveChannelOgImageDimensions(channel)
      : { width: null, height: null };
    const baseUrl = getSiteBaseUrl();

    document.title = pageTitle;

    setMetaContent(ensureMetaTag('description'), pageDescription);
    setMetaContent(ensureMetaTag('keywords'), channel ? buildChannelKeywords(channel) : DEFAULT_KEYWORDS);
    setMetaContent(ensureMetaTag('author'), SITE_NAME);
    setMetaContent(ensureMetaTag('robots'), 'index, follow, max-image-preview:large');
    setMetaContent(ensureMetaTag('theme-color'), THEME_COLOR);

    setMetaContent(ensureMetaProperty('og:type'), channel ? 'video.other' : 'website');
    setMetaContent(ensureMetaProperty('og:site_name'), SITE_NAME);
    setMetaContent(ensureMetaProperty('og:title'), pageTitle);
    setMetaContent(ensureMetaProperty('og:description'), pageDescription);
    setMetaContent(ensureMetaProperty('og:url'), canonicalUrl);
    setMetaContent(ensureMetaProperty('og:image'), imageUrl);
    setMetaContent(ensureMetaProperty('og:image:alt'), resolvedImageAlt);
    setMetaContent(ensureMetaProperty('og:locale'), 'en_GB');
    if (imageWidth) setMetaContent(ensureMetaProperty('og:image:width'), imageWidth);
    if (imageHeight) setMetaContent(ensureMetaProperty('og:image:height'), imageHeight);

    setMetaContent(ensureMetaTag('twitter:card'), 'summary_large_image');
    setMetaContent(ensureMetaTag('twitter:title'), pageTitle);
    setMetaContent(ensureMetaTag('twitter:description'), pageDescription);
    setMetaContent(ensureMetaTag('twitter:image'), imageUrl);
    setMetaContent(ensureMetaTag('twitter:image:alt'), resolvedImageAlt);

    const canonicalElement = ensureLinkRel('canonical');
    if (canonicalElement && canonicalUrl) canonicalElement.setAttribute('href', canonicalUrl);

    const organizationScript = ensureJsonLdScript('seo-organization-jsonld');
    if (organizationScript) {
      organizationScript.textContent = JSON.stringify(buildOrganizationJsonLd(baseUrl));
    }

    const websiteScript = ensureJsonLdScript('seo-website-jsonld');
    if (websiteScript) {
      websiteScript.textContent = JSON.stringify(buildWebSiteJsonLd(baseUrl));
    }

    const videoObject = channel
      ? buildVideoObjectJsonLd({ channel, canonicalUrl, absoluteBaseUrl: baseUrl })
      : null;
    if (videoObject) {
      const videoScript = ensureJsonLdScript('seo-video-jsonld');
      if (videoScript) videoScript.textContent = JSON.stringify(videoObject);
    } else {
      removeJsonLdScript('seo-video-jsonld');
    }
  } catch {
    // ignore head update failures
  }
}

export function applyHomepageSeo() {
  updateDocumentSeo({
    path: '/',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION
  });
}
